import { type QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  type ErrorComponentProps,
  Outlet,
  useLocation,
  useRouter,
} from '@tanstack/react-router'
import { type ReactNode, Suspense, useEffect } from 'react'

import {
  aplicarDesviosDeEndereco,
  chamadoEmAbertura,
  queryPedeChamado,
} from '@/lib/chamado-redirect'
import { CAMINHO_CHAMADO } from '@/lib/navegacao'
import { enderecoNovo } from '@/lib/rotas-antigas'
import { usePapeis, useSessao } from '@/lib/sessao'

import { Redirecionar } from '@/components/layout/redirecionar'
import { BlocoCodigo } from '@/components/ui/bloco-codigo'
import { Button } from '@/components/ui/button'
import { Carregando } from '@/components/ui/spinner'

import { PaginaNaoEncontrada } from '@/features/acesso/tela-aviso'
import { TelaEntrada } from '@/features/acesso/tela-entrada'

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: Raiz,
  notFoundComponent: NaoEncontrada,
  errorComponent: ErroGeral,
})

/** Telas que abrem sem login (a própria tela de entrada). */
const SEM_LOGIN = ['/entrar']

/** Espera curta enquanto a sessão carrega. */
function CarregandoSessao() {
  return (
    <div className="heimdall-ground flex min-h-svh items-center justify-center">
      <Carregando />
    </div>
  )
}

/** Carrega a sessão antes de qualquer tela (menos a de entrada). */
function Raiz() {
  const { pathname } = useLocation()
  if (SEM_LOGIN.includes(pathname)) return <Outlet />
  return (
    <Suspense fallback={<CarregandoSessao />}>
      <Portao>
        <Outlet />
      </Portao>
    </Suspense>
  )
}

/**
 * Porta do painel: conta fora do domínio ou falha da API ao liberar o acesso
 * mostram a tela de bloqueio; o link do comentário do GitLab abre o chamado.
 */
function Portao({ children }: { children: ReactNode }) {
  const sessao = useSessao()
  if (!sessao.allowed) {
    return <TelaEntrada situacao={{ tipo: 'sem-acesso', email: sessao.email }} />
  }
  if (sessao.falha_token) {
    return <TelaEntrada situacao={{ tipo: 'falha', falha: sessao.falha_token }} />
  }
  return <DesviosDoChamado>{children}</DesviosDoChamado>
}

/**
 * Link dos comentários do GitLab (`?redirect=chamado&wid=`, em qualquer página):
 * guarda o `wid` na memória da aba, tira a query do endereço e abre `/chamado`.
 * Enquanto isso, nada aparece (no painel antigo o Início nunca surgia antes do chamado).
 */
function DesviosDoChamado({ children }: { children: ReactNode }) {
  const router = useRouter()
  const { pathname, searchStr } = useLocation()
  const href = router.history.location.href
  useEffect(() => {
    aplicarDesviosDeEndereco(router.history, href)
  }, [href, router])
  const abrindo =
    pathname !== CAMINHO_CHAMADO && (queryPedeChamado(searchStr) || chamadoEmAbertura())
  if (abrindo) return null
  return <>{children}</>
}

/**
 * Endereço que não é de nenhuma tela: se for de uma tela do painel antigo
 * (ex.: `/violacoes`), vai para a página nova correspondente, mantendo os
 * parâmetros; senão mostra "Página não encontrada".
 */
function NaoEncontrada() {
  return (
    <Suspense fallback={<CarregandoSessao />}>
      <Portao>
        <DestinoOuAviso />
      </Portao>
    </Suspense>
  )
}

function DestinoOuAviso() {
  const { pathname, searchStr } = useLocation()
  const papeis = usePapeis()
  const novo = enderecoNovo(pathname, searchStr, papeis)
  if (novo) return <Redirecionar para={novo} />
  return <PaginaNaoEncontrada endereco={`${pathname}${searchStr}`} />
}

/** Erro inesperado numa tela: a mensagem aparece inteira, em bloco de código. */
function ErroGeral({ error }: ErrorComponentProps) {
  return (
    <div className="heimdall-ground flex min-h-svh items-center justify-center p-3">
      <section className="heimdall-block flex w-full max-w-2xl flex-col gap-4 p-6 md:p-8">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">Esta tela encontrou um erro</h1>
          <p className="text-sm text-muted-foreground">
            Recarregue a página. Se o erro continuar, avise quem cuida do Heimdall e envie a
            mensagem abaixo.
          </p>
        </div>
        <BlocoCodigo>{error instanceof Error ? error.message : String(error)}</BlocoCodigo>
        <Button
          className="heimdall-botao h-11 w-fit rounded-full px-5"
          onClick={() => window.location.reload()}
        >
          Recarregar
        </Button>
      </section>
    </div>
  )
}
